import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { endOfDay, parseISO, startOfDay } from 'date-fns'
import { Fragment, useCallback, useMemo, useReducer } from 'react'
import { MdPerson } from 'react-icons/md'
// @ts-expect-error // No types
import PartnerOrganizations from 'shared_src/dictionaries/partnerOrganizations'
import { useDebounceValue } from 'usehooks-ts'

import { SystemRoles } from '../../../lib/Codes'
import groups from '../../../lib/Codes/groups'
import http from '../../../lib/http'
import { useAppSelector } from '../../../store'
import * as Table from '../../claim-history/simple-table'
import * as Grid from '../../shared/grid'
import { PageHeader } from '../components/page-header'
import styles from '../index.module.css'

let roles: Role[] = SystemRoles
let organizations: Organization[] = PartnerOrganizations

interface Role {
  code: string
  value: string
  label: string
}

interface Organization {
  value: string
  label: string
}

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  created_at?: string | null
  author?: string | null
  author_name?: string | null
  type?: ObjectType | null
  resource?: string | null
  resource_name?: string | null
  action?: HistoryAction | null
  data?: {
    changes?: Change[]
  }
  is_mobile?: boolean | null
}

interface Change {
  old?: unknown
  new?: unknown
  name: string
  description?: string
}

interface User {
  id: string
  first_name?: string | null
  last_name?: string | null
}

const enum ObjectType {
  UserMetadata = 'user_metadata',
  Settings = 'settings',
  Document = 'document'
}

let objectTypeMapper: Record<ObjectType, string> = {
  [ObjectType.UserMetadata]: 'User',
  [ObjectType.Settings]: 'Settings',
  [ObjectType.Document]: 'Document'
}

const enum Group {
  Compassion = 'compassion',
  Mobile = 'mobile'
}

let groupMapper: Record<Group, string> = {
  [Group.Compassion]: 'Compassion',
  [Group.Mobile]: 'Mobile'
}

const enum HistoryAction {
  Create = 'create',
  Update = 'update',
  Download = 'download',
  ViewReport = 'view_report',
}

let actionMapper: Record<HistoryAction, string> = {
  [HistoryAction.Create]: 'Create',
  [HistoryAction.Update]: 'Update',
  [HistoryAction.Download]: 'Download',
  [HistoryAction.ViewReport]: 'View Report'
}

type QueryKey = [
  'audit-log',
  SearchParams
]

interface Resource {
  id: string
  name: string
  type: string
}

interface QueryData {
  data: Entry[]
  filters: {
    resources: Resource[]
  }
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    from?: string
    to?: string
    group: Group[]
    resource: string[]
    author: string[]
    type: ObjectType[]
    action: HistoryAction[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'created_at'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      group: [
        Group.Compassion
      ],
      resource: [],
      author: [],
      type: [],
      action: []
    },
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  patient_name: 'patient_last',
  author_name: 'author_last'
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.action.length === initialState.searchParams.filters.action.length &&
    searchParams.filters.action.every(v => initialState.searchParams.filters.action.includes(v)) &&
    searchParams.filters.group.length === initialState.searchParams.filters.group.length &&
    searchParams.filters.group.every(v => initialState.searchParams.filters.group.includes(v)) &&
    searchParams.filters.author.length === initialState.searchParams.filters.author.length &&
    searchParams.filters.author.every(v => initialState.searchParams.filters.author.includes(v)) &&
    searchParams.filters.type.length === initialState.searchParams.filters.type.length &&
    searchParams.filters.type.every(v => initialState.searchParams.filters.type.includes(v))
  )
}

export function AuditLogPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState, state => ({
    ...state,
    searchParams: {
      ...state.searchParams,
      filters: {
        ...state.searchParams.filters,
        from: new Date().toISOString().substring(0, 10)
      }
    }
  }))

  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'audit-log',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...(filters.from ? [[ 'filters.from', startOfDay(parseISO(filters.from)).toISOString() ]] : []),
        ...(filters.to ? [[ 'filters.to', endOfDay(parseISO(filters.to)).toISOString() ]] : []),
        ...filters.action.map(v => [ 'filters.action', v ]),
        ...filters.group.map(v => [ 'filters.group', v ]),
        ...filters.author.map(v => [ 'filters.author', v ]),
        ...filters.type.map(v => [ 'filters.type', v ]),
        ...(filters.resource.length > 0 ? filters.resource.map(v => [ 'filters.resource', v ]) : []),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/log/records?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let allUsersQuery = useQuery<unknown, unknown, { data: User[] }>({
    queryKey: [
      'users'
    ],
    async queryFn () {
      return await http.getRequest('/admin/users?count=9999&page=0&on=first_name&dir=asc')
    }
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        if (!entry.created_at) {
          return null
        }

        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'author',
      header: 'Author',
      isSortable: false,
      getValue (entry) {
        return entry.author_name
      }
    },
    {
      name: 'type',
      header: 'Type',
      isSortable: false,
      getValue (entry) {
        if (!entry.type) {
          return null
        }

        return objectTypeMapper[entry.type] ?? entry.type
      }
    },
    {
      name: 'resource_name',
      header: 'Resource',
      isSortable: false
    },
    {
      name: 'action',
      header: 'Action',
      isSortable: false,
      getValue (entry) {
        if (!entry.action) {
          return null
        }

        return actionMapper[entry.action] ?? entry.action
      }
    },
    {
      name: 'group',
      header: 'Group',
      isSortable: false,
      getValue (entry) {
        if (entry.is_mobile === true) {
          return groupMapper[Group.Mobile]
        }

        return groupMapper[Group.Compassion]
      }
    }
  ], [])

  let filters = useMemo<Table.Filter[]>(() => {
    return [
      {
        name: 'type',
        value: state.searchParams.filters.type,
        options: [
          { value: ObjectType.UserMetadata, label: objectTypeMapper[ObjectType.UserMetadata] ?? ObjectType.UserMetadata },
          { value: ObjectType.Settings, label: objectTypeMapper[ObjectType.Settings] ?? ObjectType.Settings },
          { value: ObjectType.Document, label: objectTypeMapper[ObjectType.Document] ?? ObjectType.Document }
        ]
      },
      {
        name: 'action',
        value: state.searchParams.filters.action,
        options: [
          { value: HistoryAction.Create, label: actionMapper[HistoryAction.Create] ?? HistoryAction.Create },
          { value: HistoryAction.Update, label: actionMapper[HistoryAction.Update] ?? HistoryAction.Update },
          { value: HistoryAction.Download, label: actionMapper[HistoryAction.Download] ?? HistoryAction.Download }
        ]
      },
      {
        name: 'group',
        value: state.searchParams.filters.group,
        options: [
          { value: Group.Compassion, label: groupMapper[Group.Compassion] ?? Group.Compassion },
          { value: Group.Mobile, label: groupMapper[Group.Mobile] ?? Group.Mobile }
        ]
      },
      {
        name: 'author',
        value: state.searchParams.filters.author,
        options: allUsersQuery.data?.data.map(user => ({
          value: user.id,
          label: [ user.first_name, user.last_name ].filter(Boolean).join(' ')
        })) ?? []
      },
      {
        name: 'resource_name',
        value: state.searchParams.filters.resource,
        options: query.data?.filters.resources.map(resource => ({
          value: resource.id,
          searchBy: resource.name,
          label: (
            <span
              className={cx(
                'flex flex-row items-center justify-start gap-1',
                resource.type === 'patient' && 'text-[#dfa450]'
              )}
            >
              <span>
                {resource.name}
              </span>

              {resource.type === 'patient' && (
                <MdPerson
                  size={18}
                  title='Patient'
                />
              )}
            </span>
          )
        })) ?? []
      },
      {
        name: 'created_at',
        isActive: typeof state.searchParams.filters.from === 'string' || typeof state.searchParams.filters.to === 'string',
        render ({ onChange, onClose }) {
          return (
            <div style={{ padding: '2.5rem 1rem 2rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Grid.Input
                type='date'
                placeholder='From'
                value={state.searchParams.filters.from ?? ''}
                onChange={value => {
                  dispatch({
                    type: 'filters/merge',
                    payload: {
                      from: value || undefined
                    }
                  })
                }}
              />

              <Grid.Input
                type='date'
                placeholder='To'
                value={state.searchParams.filters.to ?? ''}
                onChange={value => {
                  dispatch({
                    type: 'filters/merge',
                    payload: {
                      to: value || undefined
                    }
                  })
                }}
              />
            </div>
          )
        }
      }
    ]
  }, [ state.searchParams.filters, allUsersQuery.data, query.data?.filters.resources ])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    let mapper: Record<string, string> = {
      resource_name: 'resource'
    }

    dispatch({
      type: 'filters/merge',
      payload: {
        [mapper[name as string] ?? name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Audit Log</span>
        </h1>
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No records found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, state, setState) => {
          return (
            <ExpandingComponent
              item={item}
              users={allUsersQuery.data?.data}
              // onAction={() => {
              //   query.refetch()
              //     .then(() => {
              //       setState(item.id)
              //     })
              //     .catch(err => {
              //       console.error(err)
              //       Sentry.captureException(err)
              //     })
              // }}
            />
          )
        }}
      />
    </div>
  )
}

export function stringifyValue (value: unknown): string | null {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number') {
    return value.toString(10)
  }

  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled'
  }

  if (Array.isArray(value)) {
    return value.map(stringifyValue).join(', ')
  }

  if (typeof value === 'object' && value !== null) {
    if (
      'id' in value &&
      'first_name' in value &&
      'last_name' in value
    ) {
      let user = value as User
      return [ user.first_name, user.last_name ].filter(Boolean).join(' ')
    }

    return JSON.stringify(value)
  }

  return null
}

interface ExpandingComponentProps {
  item: Entry
  users?: User[]
}

function ExpandingComponent ({ item, users }: ExpandingComponentProps): JSX.Element {
  let organizationsMap = useAppSelector(state => state.globalVars.organizations ?? {})

  let changes = item.data?.changes?.filter(change => {
    if (typeof change.old !== 'undefined' && typeof change.new !== 'undefined') {
      return stringifyValue(change.old) !== stringifyValue(change.new)
    }

    return !!change.new
  }) ?? []

  if (!changes.length) {
    return (
      <div className={styles.expandableRowContent}>
        <div className={styles.row}>
          <div className={styles.details}>
            <span className={styles.label}>No changes</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.expandableRowContent}>
      <div className={styles.row}>
        <div className={styles.details}>
          {changes.map((change, index) => (
            <Fragment key={index}>
              <span className={styles.label}>{change.description}</span>
              <span className={styles.value}>
                {(() => {
                  let oldValue = stringifyValue(change.old)
                  let newValue = stringifyValue(change.new)

                  if (change.name === 'organizations') {
                    oldValue = (change.old as string[])?.map(id => organizationsMap[id]?.name).filter(Boolean).join(', ')
                    newValue = (change.new as string[])?.map(id => organizationsMap[id]?.name).filter(Boolean).join(', ')
                  }

                  if (change.name === 'role') {
                    oldValue = roles.find(role => role.code === change.old)?.label ?? null
                    newValue = roles.find(role => role.code === change.new)?.label ?? null
                  }

                  switch (change.name) {
                    case 'organization':
                      oldValue = organizations.find(organization => organization.value === change.old)?.label ?? null
                      newValue = organizations.find(organization => organization.value === change.new)?.label ?? null
                      break

                    case 'organizations':
                      oldValue = (change.old as string[])?.map(id => organizationsMap[id]?.name).filter(Boolean).join(', ')
                      newValue = (change.new as string[])?.map(id => organizationsMap[id]?.name).filter(Boolean).join(', ')
                      break

                    case 'role':
                      oldValue = roles.find(role => role.code === change.old)?.label ?? null
                      newValue = roles.find(role => role.code === change.new)?.label ?? null
                      break

                    case 'group':
                      oldValue = groups.find(group => group.value === change.old)?.label ?? null
                      newValue = groups.find(group => group.value === change.new)?.label ?? null
                      break

                    case 'supervisor':
                    case 'director':
                      oldValue = stringifyValue(users?.find(user => user.id === change.old))
                      newValue = stringifyValue(users?.find(user => user.id === change.new))
                  }

                  if (
                    typeof change.old !== 'undefined' &&
                    typeof change.new !== 'undefined' &&
                    (change.new === null || change.new === '')
                  ) {
                    return (
                      <span className={styles.deletedValue}>
                        {(oldValue ?? '') || '(Empty)'}
                      </span>
                    )
                  }

                  if (
                    typeof change.old !== 'undefined' &&
                    typeof change.new !== 'undefined'
                  ) {
                    return (
                      <div className={styles.modifiedValue}>
                        <span>
                          {(oldValue ?? '') || '(Empty)'}
                        </span>

                        <span>
                          {' '}→{' '}
                        </span>

                        <span>
                          {(newValue ?? '') || '(Empty)'}
                        </span>
                      </div>
                    )
                  }

                  if (
                    typeof change.old === 'undefined'
                  ) {
                    return (
                      <span className={styles.addedValue}>
                        {(newValue ?? '') || '(Empty)'}
                      </span>
                    )
                  }

                  return null
                })()}
                {/* {typeof change.old !== 'undefined' && typeof change.new !== 'undefined' && (
                  <div>
                    <span
                      style={{ marginRight: '1rem' }}
                    >
                      {JSON.stringify(change.old)}
                    </span>
                    <span>
                      {JSON.stringify(change.new)}
                    </span>
                  </div>
                )} */}
              </span>
            </Fragment>
          ))}
          {/* <span className={styles.label}>Name</span>
          <span className={styles.value}>
            {[ item.first_name, item.last_name ].filter(Boolean).join(' ')}
          </span> */}
        </div>
      </div>
    </div>
  )
}
